import { z } from "zod";

/**
 * Log levels an operator may set on a deployment instance
 * (`deployment_instances.override_log_level`). The column is free text; this
 * is the boundary check for both relay routes and the value the engine pushes
 * to a running worker, and every entry is one the worker's `parseLogLevel`
 * understands.
 */
export const INSTANCE_LOG_LEVELS = [
  "trace",
  "debug",
  "info",
  "warn",
  "error",
  "fatal",
] as const;
export const instanceLogLevelSchema = z.enum(INSTANCE_LOG_LEVELS);
export type InstanceLogLevel = z.infer<typeof instanceLogLevelSchema>;

/**
 * Output schemas for the single-instance detail router (`deploymentInstances.*`).
 *
 * The `get` (redacted) and `getAdmin` (full) procedures return the SAME
 * `DeploymentInstanceDetailItem` type from one `getInstance` call — redaction
 * happens inside the service by `isSuperAdmin`. So one schema must accept both:
 *   - super-admin-only fields (`Partial<DeploymentInstanceSuperAdminFields>`)
 *     are OMITTED for redacted viewers → `.optional()`.
 *   - the `adminDetails` / `controlTower` blocks and `fleetGpuInstance.adminDetails`
 *     are NULLED for redacted viewers → `.nullable()`.
 *   - `engineConfig` is present for every viewer, so it is `.nullable()` only
 *     for shape parity with the other detail blocks.
 */

/** Mirrors the `deployment_instance_status` pgEnum (`@kuzco/db`) exactly. */
export const DEPLOYMENT_INSTANCE_STATUS_VALUES = [
  "Provisioning",
  "Initializing",
  "DownloadingModelWeights",
  "StartingEngine",
  "Running",
  "Unhealthy",
  "Updating",
  "Draining",
  "ShuttingDown",
  "Terminated",
  "Unschedulable",
] as const;

export type DeploymentInstanceStatus =
  (typeof DEPLOYMENT_INSTANCE_STATUS_VALUES)[number];

const deploymentInstanceStatusSchema = z.enum(
  DEPLOYMENT_INSTANCE_STATUS_VALUES,
);

/**
 * Worker-reported lifecycle state (the `engine_state` jsonb), written by the
 * engine on every heartbeat. Only the fields the dashboard and CLI read are
 * modelled; the rest is engine bookkeeping and stays unvalidated passthrough.
 * Every field is optional: a non-running row carries `{}`.
 */
export const deploymentInstanceEngineStateSchema = z
  .object({
    status: z.string().optional(),
    configured_shards: z.number().optional(),
    connected_shards: z.number().optional(),
    ready_shards: z.number().optional(),
    draining_shards: z.number().optional(),
    boot_started_at_ms: z.number().optional(),
    ready_at_ms: z.number().optional(),
    reported_at_ms: z.number().optional(),
    inflight_generations: z.number().optional(),
    inflight_sampled_at_ms: z.number().optional(),
    active_generations: z.number().optional(),
    weights_percent: z.number().optional(),
    weights_downloaded_bytes: z.number().optional(),
    weights_total_bytes: z.number().optional(),
    weights_eta_seconds: z.number().nullable().optional(),
  })
  .passthrough();
export type DeploymentInstanceEngineStateView = z.infer<
  typeof deploymentInstanceEngineStateSchema
>;

/** Always-visible fleet-instance summary. `instanceDegradedAt` is a Date. */
const fleetGpuInstanceSummarySchema = z.object({
  fleetInstanceId: z.string(),
  instanceGeoLocation: z.string().nullable(),
  instanceDegradedAt: z.date().nullable(),
  cardSlug: z.string(),
  cardCount: z.number(),
  vramBytesPerCard: z.number(),
});

/** Super-admin-only fleet fields; null for redacted viewers. */
const fleetGpuInstanceAdminFieldsSchema = z.object({
  ipAddress: z.string().nullable(),
  providerRegion: z.string(),
  provider: z.string(),
  sshSupported: z.boolean(),
  sshPort: z.number().nullable(),
  sshUsername: z.string().nullable(),
  externalProviderId: z.string().nullable(),
  externalProviderSKU: z.string().nullable(),
  externalProviderHourlyCostCents: z.number().nullable(),
});

/** Super-admin-only joined control-tower summary; null for redacted viewers. */
const controlTowerSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
});

/**
 * The engine configuration the instance resolved to. Present for every viewer
 * who can read the instance, because teams configure the engine on their own
 * deployments. `flags`/`environmentVariables` are string maps; `configFiles`
 * and `instanceRoutingEngineShardConfig` are opaque JSON (`z.unknown()`).
 */
const deploymentInstanceEngineConfigSchema = z.object({
  modelConfigId: z.string(),
  modelConfigVersion: z.number(),
  flags: z.record(z.string(), z.string()),
  configFiles: z.unknown(),
  environmentVariables: z.record(z.string(), z.string()),
  parallelism: z.number(),
  instanceRoutingEngineShardConfig: z.unknown(),
});

/**
 * Super-admin-only operational block; null for redacted viewers.
 * `instanceReportedGPUCount` is a `bigint` column serialized as a STRING;
 * `updatedAt`/`terminatedAt` are Date objects.
 */
const deploymentInstanceDetailAdminFieldsSchema = z.object({
  instanceReportedGPUSlug: z.string().nullable(),
  instanceReportedGPUCount: z.string().nullable(),
  isRunningInDocker: z.boolean(),
  dockerImage: z.string().nullable(),
  dockerImageTag: z.string().nullable(),
  updatedAt: z.date(),
  terminatedAt: z.date().nullable(),
});

/**
 * `deploymentInstances.get` (redacted) and `.getAdmin` (full). Common fields
 * and `engineConfig` are always present; the `Partial<SuperAdminFields>` block
 * is `.optional()` (absent for redacted viewers); the admin blocks are
 * `.nullable()`.
 */
export const deploymentInstanceDetailItemSchema = z.object({
  // ── Always-visible (DeploymentInstanceCommon) ──
  id: z.string(),
  configHash: z.string(),
  status: deploymentInstanceStatusSchema,
  statusMetadata: z.unknown(),
  engineState: deploymentInstanceEngineStateSchema.optional(),
  parallelism: z.number(),
  stopReason: z.string().nullable(),
  stopSource: z.string().nullable(),
  cliVersion: z.string().nullable(),
  cliUpdatePreviousVersion: z.string().nullable(),
  cliUpdateTargetVersion: z.string().nullable(),
  cliUpdateStartedAt: z.date().nullable(),
  createdByUserId: z.string().nullable(),
  createdAt: z.date(),
  // ── Partial<DeploymentInstanceSuperAdminFields> — omitted when redacted ──
  code: z.string().optional(),
  isManualInstance: z.boolean().optional(),
  cliVersionHash: z.string().nullable().optional(),
  engine: z.string().optional(),
  engineVersion: z.string().nullable().optional(),
  overrideLogLevel: z.string().nullable().optional(),
  overrideDesiredCliVersion: z.string().nullable().optional(),
  dockerImage: z.string().nullable().optional(),
  dockerImageTag: z.string().nullable().optional(),
  // ── Detail-only ──
  deploymentId: z.string(),
  fleetGpuInstance: fleetGpuInstanceSummarySchema
    .extend({ adminDetails: fleetGpuInstanceAdminFieldsSchema.nullable() })
    .nullable(),
  controlTower: controlTowerSummarySchema.nullable(),
  engineConfig: deploymentInstanceEngineConfigSchema.nullable(),
  adminDetails: deploymentInstanceDetailAdminFieldsSchema.nullable(),
});

/**
 * `deploymentInstances.listLive` item: a non-Terminated instance with the
 * deployment and the endpoint models it serves (super-admin fleet view).
 */
export const fleetDeploymentInstanceSchema = z.object({
  id: z.string(),
  status: deploymentInstanceStatusSchema,
  engineState: deploymentInstanceEngineStateSchema,
  parallelism: z.number(),
  createdAt: z.date(),
  deploymentId: z.string(),
  deploymentName: z.string(),
  publicModelIdentifiers: z.array(z.string()),
});

/**
 * One gauge in the instance metrics response (`deploymentInstances.getMetrics`).
 * ClickHouse-sourced: `latest` and each `value` are nullable numbers, `ts` is a
 * string bucket timestamp.
 */
const gaugeMetricSchema = z.object({
  latest: z.number().nullable(),
  series: z.array(z.object({ ts: z.string(), value: z.number().nullable() })),
});

/** The six sparkline gauges (`deploymentInstances.getMetrics`). */
export const deploymentInstanceMetricsResponseSchema = z.object({
  gpuUtilization: gaugeMetricSchema,
  gpuMemoryUsedBytes: gaugeMetricSchema,
  gpuMemoryTotalBytes: gaugeMetricSchema,
  cpuUtilization: gaugeMetricSchema,
  memoryUsedBytes: gaugeMetricSchema,
  memoryTotalBytes: gaugeMetricSchema,
});

/**
 * One OTel log row (`deploymentInstances.getLogs`). ClickHouse-sourced:
 * `timestamp`/`observed_timestamp` are string timestamps, `severity_number` is
 * a number, and the attribute maps are string→string records.
 */
const otelLogRowSchema = z.object({
  source_id: z.string(),
  timestamp: z.string(),
  observed_timestamp: z.string(),
  severity_number: z.number(),
  severity_text: z.string(),
  body: z.string(),
  trace_id: z.string(),
  span_id: z.string(),
  event_name: z.string(),
  resource_attributes: z.record(z.string(), z.string()),
  attributes: z.record(z.string(), z.string()),
  scope_name: z.string(),
  scope_version: z.string(),
  service_name: z.string(),
});

/** Paginated instance logs envelope (`deploymentInstances.getLogs`). */
export const deploymentInstanceLogsResponseSchema = z.object({
  data: z.array(otelLogRowSchema),
  meta: z.object({
    limit: z.number(),
    offset: z.number(),
    has_more: z.boolean(),
  }),
});
